import type { Hotel } from "@/types/services/hotel/apis/Availability";
import type { Flight } from "@/types/services/flight/apis/Availability";
import type { TourAvailabilityItem } from "@/types/services/tour/apis/Availability";
import { isNumber, isString, isUndefined } from "./type-utils";
import type {
  FilterValueMap,
  TypedFilter,
} from "../components/filters/filter.types";
import type { TourItem } from "@/types/services/tour/apis/Detail";

/**
 * Generic sort function for arrays of objects.
 */
export function sortItems<T>(
  items: T[],
  fieldName: keyof T,
  order: "asc" | "desc" = "asc",
): T[] {
  const sortedItems = [...items];
  return sortedItems.sort((a, b) => {
    const aValue = a[fieldName];
    const bValue = b[fieldName];
    if (isNumber(aValue) && isNumber(bValue)) {
      return order === "asc" ? aValue - bValue : bValue - aValue;
    }
    return order === "asc"
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });
}

/**
 * Generic filter pipeline: applies an array of filter functions in sequence.
 */
export function applyFilters<T>(
  items: T[],
  filters: ((items: T[]) => T[])[],
): T[] {
  return filters.reduce((result, filter) => filter(result), items);
}

/**
 * Filter by stars (expects stars as string or number).
 */
export function filterByStars<X extends { stars: string | number }>(
  items: X[],
  stars: number,
): X[] {
  return items.filter((item) => +item.stars === stars);
}

/**
 * Filter by price range.
 */
export function filterByPrice<X>(
  items: X[],
  range: { minPrice: number; maxPrice: number },
  fieldName: keyof X,
): X[] {
  if (range.minPrice > range.maxPrice) {
    throw new Error("minPrice cannot be greater than maxPrice");
  }
  return items.filter((item) => {
    const value = item[fieldName];
    const numericValue = typeof value === "number" ? value : Number(value);
    return (
      isNumber(numericValue) &&
      numericValue >= range.minPrice &&
      numericValue <= range.maxPrice
    );
  });
}

/**
 * Filter by query string on a given field.
 */
export function filterByQuery<X>(
  items: X[],
  query: string,
  fieldName: keyof X,
): X[] {
  const queryWords = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (queryWords.length === 0) return items;
  return items.filter((item) => {
    const value = item[fieldName];
    const searchableValue = (
      typeof value === "string"
        ? value
        : typeof value === "number"
          ? value.toString()
          : ""
    ).toLowerCase();
    return queryWords.every((word) => searchableValue.includes(word));
  });
}

/**
 * Filter flights by departure time period.
 */
export function filterByTime<X>(
  items: X[],
  time: string,
  fieldName: keyof X,
): X[] {
  return items.filter((item) => {
    const itemTime = item[fieldName];

    if (isString(itemTime)) {
      const [hours, minutes] = itemTime.split(":").map(Number);
      const totalMinutes = hours * 60 + minutes;
      switch (time) {
        case "morning":
          return totalMinutes >= 6 * 60 && totalMinutes < 12 * 60;
        case "afternoon":
          return totalMinutes >= 12 * 60 && totalMinutes < 17 * 60;
        case "evening":
          return totalMinutes >= 17 * 60 && totalMinutes < 21 * 60;
        case "night":
          return totalMinutes >= 21 * 60 || totalMinutes < 6 * 60;
        default:
          return false;
      }
    }

    return item;
  });
}

/**
 * Filter by airline code.
 */
export function filterByAirline<X>(
  items: X[],
  airlines: string,
  fieldName: keyof X,
): X[] {
  const AIRLINES = airlines.split(",");

  return items.filter((item) => {
    if (isString(item[fieldName]))
      return item?.[fieldName] && AIRLINES.includes(item[fieldName]);

    return item;
  });
}

/**
 * Filter by cabin type.
 */
export function filterByCabinType<X extends { cabin_type: string }>(
  items: X[],
  cabinType: string,
): X[] {
  const FLIGHT_CLASSES = cabinType.split(",");
  return items.filter(
    (item) => item?.cabin_type && FLIGHT_CLASSES.includes(item.cabin_type),
  );
}

/**
 * Filter by ticket type.
 */
export function filterByTicketType<X extends { type: string }>(
  items: X[],
  ticketsType: string,
): X[] {
  const TICKET_TYPE = ticketsType.split(",");
  return items.filter((item) => item?.type && TICKET_TYPE.includes(item.type));
}

/**
 * Sorting and filtering config for each entity type.
 */
const entityConfigs = {
  hotel: {
    sort: <X extends Hotel>(items: X[], sort: string): X[] => {
      if (sort.includes("star")) {
        return sortItems(items, "stars", sort === "max_star" ? "desc" : "asc");
      }
      return sortItems(
        items,
        "min_price",
        sort === "max_price" ? "desc" : "asc",
      );
    },
    priceField: "min_price" as keyof Hotel,
    queryField: "name" as keyof Hotel,
    starsField: "stars" as keyof Hotel,
  },
  tour: {
    sort: <X extends TourAvailabilityItem>(items: X[], sort: string): X[] => {
      if (sort.includes("star")) {
        return sortItems(items, "stars", sort === "max_star" ? "desc" : "asc");
      }
      return sortItems(items, "price", sort === "max_price" ? "desc" : "asc");
    },
    priceField: "price" as keyof TourAvailabilityItem,
    queryField: "name" as keyof TourAvailabilityItem,
    starsField: "stars" as keyof TourAvailabilityItem,
  },
  tourDetail: {
    sort: <X extends TourItem>(items: X[], sort: string): X[] => {
      // if (sort.includes("star")) {
      //   return sortItems(items, "stars", sort === "max_star" ? "desc" : "asc");
      // }
      return sortItems(items, "price", sort === "max_price" ? "desc" : "asc");
    },
    priceField: "price" as keyof TourItem,
    queryField: "name" as keyof TourItem,
    starsField: "stars" as keyof TourItem,
  },
  flight: {
    sort: <X extends Flight>(items: X[], sort: string): X[] => {
      if (sort.includes("price")) {
        return sortItems(items, "price", sort === "max_price" ? "desc" : "asc");
      }
      return sortItems(
        items,
        "departure_time",
        sort === "earliest" ? "asc" : "desc",
      );
    },
    priceField: "price" as keyof Flight,
    queryField: "flight_no" as keyof Flight,
  },
};

/**
 * Generic filter function for hotels.
 */
export function filterHotels<X extends Hotel>(
  hotels: X[],
  searchParams: {
    sort?: string;
    stars?: number | string;
    price?: string;
    query?: string;
  },
): X[] {
  const { sort, stars, price, query } = searchParams;
  const filters: ((items: X[]) => X[])[] = [];

  if (sort) {
    filters.push((items) => entityConfigs.hotel.sort(items, sort));
  }
  if (!isUndefined(stars) && +stars > 0) {
    filters.push((items) => filterByStars(items, +stars));
  }
  if (price) {
    const [minPrice, maxPrice] = price.split("-");
    filters.push((items) =>
      filterByPrice(
        items,
        { minPrice: +minPrice, maxPrice: +maxPrice },
        entityConfigs.hotel.priceField,
      ),
    );
  }
  if (query) {
    filters.push((items) =>
      filterByQuery(items, query, entityConfigs.hotel.queryField),
    );
  }
  return applyFilters(hotels, filters);
}

/**
 * Generic filter function for tours.
 */
export function filterTours<X extends TourAvailabilityItem>(
  tours: X[],
  searchParams: {
    sort?: string;
    stars?: number | string;
    price?: string;
    query?: string;
  },
): X[] {
  const { sort, stars, price, query } = searchParams;
  const filters: ((items: X[]) => X[])[] = [];

  if (sort) {
    filters.push((items) => entityConfigs.tour.sort(items, sort));
  }
  if (!isUndefined(stars) && +stars > 0) {
    filters.push((items) => filterByStars(items, +stars));
  }
  if (price) {
    const [minPrice, maxPrice] = price.split("-");
    filters.push((items) =>
      filterByPrice(
        items,
        { minPrice: +minPrice, maxPrice: +maxPrice },
        entityConfigs.tour.priceField,
      ),
    );
  }
  if (query) {
    filters.push((items) =>
      filterByQuery(items, query, entityConfigs.tour.queryField),
    );
  }

  return applyFilters(tours, filters);
}

export function filterTourDetail<X extends TourItem>(
  tours: X[],
  searchParams: {
    sort?: string;
    stars?: number | string;
    price?: string;
    query?: string;
    departureTime: string;
    airline: string;
  },
): X[] {
  const { sort, stars, price, query, departureTime, airline } = searchParams;
  const filters: ((items: X[]) => X[])[] = [];

  if (sort) {
    filters.push((items) => entityConfigs.tourDetail.sort(items, sort));
  }

  if (price) {
    const [minPrice, maxPrice] = price.split("-");
    filters.push((items) =>
      filterByPrice(
        items,
        { minPrice: +minPrice, maxPrice: +maxPrice },
        entityConfigs.tourDetail.priceField,
      ),
    );
  }
  if (query) {
    filters.push((items) =>
      filterByQuery(items, query, entityConfigs.tourDetail.queryField),
    );
  }

  // if (departureTime) {
  //   filters.push((items) => filterByTime(items, departureTime, "departure"));
  // }
  // if (airline) {
  //   filters.push((items) => filterByAirline(items, airline));
  // }

  return applyFilters(tours, filters);
}

/**
 * Generic filter function for flights.
 */
export function filterFlights<X extends Flight>(
  flights: X[],
  searchParams: {
    sort?: string;
    price?: string;
    query?: string;
    airline?: string;
    departureTime?: string;
    classes?: string;
    cabinType?: string;
  },
): X[] {
  const { sort, price, query, airline, departureTime, classes, cabinType } =
    searchParams;
  const filters: ((items: X[]) => X[])[] = [];

  // Sold-out tickets stay visible in the unfiltered default list, but should
  // never be part of a filtered or sorted result set.
  const hasActiveFilterOrSort = Boolean(
    sort || price || query || airline || departureTime || classes || cabinType,
  );

  if (hasActiveFilterOrSort) {
    filters.push((items) =>
      items.filter(
        ({ price, capacity }) =>
          typeof price === "number" && Number(capacity) > 0,
      ),
    );
  }

  // "default" is a display label, not a sorting direction.  An explicitly
  // selected sort takes priority over the departure-time filter's ordering.
  const hasExplicitSort = Boolean(sort && sort !== "default");

  if (sort && sort !== "default") {
    filters.push((items) => entityConfigs.flight.sort(items, sort));
  }
  if (departureTime) {
    filters.push((items) =>
      filterByTime(items, departureTime, "departure_time"),
    );

    if (!hasExplicitSort) {
      filters.push((items) => sortItems(items, "departure_time", "asc"));
    }
  }
  if (airline) {
    filters.push((items) => filterByAirline(items, airline, "airline_name"));
  }
  if (cabinType) {
    filters.push((items) => filterByCabinType(items, cabinType));
  }
  if (classes) {
    filters.push((items) => filterByTicketType(items, classes));
  }
  if (price) {
    const [minPrice, maxPrice] = price.split("-");
    filters.push((items) =>
      filterByPrice(
        items,
        { minPrice: +minPrice, maxPrice: +maxPrice },
        entityConfigs.flight.priceField,
      ),
    );
  }
  if (query) {
    filters.push((items) =>
      filterByQuery(items, query, entityConfigs.flight.queryField),
    );
  }
  return applyFilters(flights, filters);
}

/**
 * Map filters from JSON and filter values.
 */
export function mapFiltersFromJson(
  filters: {
    name: string;
    label: string;
    placeholder?: string;
    value: unknown;
    id: string;
  }[],
  filterValues: Partial<FilterValueMap>,
): TypedFilter[] {
  return filters.map((filter) => ({
    ...filter,
    value: filterValues[filter.name as keyof FilterValueMap] ?? filter.value,
  })) as TypedFilter[];
}
